import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { listFolders } from "@/features/documents/api";
import type { DocumentFolder } from "@/features/documents/types";
import { generateSubmissionPdf } from "@/features/forms/api";
import type { FormSubmission } from "@/features/forms/types";
import { ApiError } from "@/lib/api";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  submission: FormSubmission;
  clientId: string;
  onSaved: (submission: FormSubmission) => void;
}

/**
 * "Save to Documents" (legacy `generate_pdf.php`): confirm the document name +
 * target folder before filling → flattening → filing the PDF into the client's
 * archive. The overwrite checkbox appears only when the backend reports a name
 * collision (409), and locks in the replace on the next confirm.
 */
export function SaveToDocumentsDialog({
  open,
  onOpenChange,
  submission,
  clientId,
  onSaved,
}: Props) {
  const [folders, setFolders] = useState<DocumentFolder[]>([]);
  const [name, setName] = useState("");
  const [folderId, setFolderId] = useState<string>("");
  const [overwrite, setOverwrite] = useState(false);
  const [collision, setCollision] = useState(false);
  const [saving, setSaving] = useState(false);

  // Reset + load folders each time the dialog opens.
  useEffect(() => {
    if (!open) return;
    setName(`Form ${submission.formNumber} - ${submission.formTitle}`);
    setOverwrite(false);
    setCollision(false);
    listFolders(clientId)
      .then((all) => {
        setFolders(all);
        const forms = all.find((f) => f.systemKey === "FORMS");
        setFolderId(forms?.id ?? all[0]?.id ?? "");
      })
      .catch(() => toast.error("Couldn't load document folders."));
  }, [open, clientId, submission.formNumber, submission.formTitle]);

  async function handleConfirm() {
    setSaving(true);
    try {
      const updated = await generateSubmissionPdf(submission.id, {
        documentName: name.trim(),
        folderId: folderId || null,
        overwrite,
      });
      onSaved(updated);
      toast.success("Saved to Documents");
      onOpenChange(false);
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        // Name already taken in this folder — reveal the overwrite option.
        setCollision(true);
        setOverwrite(true);
        toast.error("A document with that name already exists — confirm overwrite.");
      } else {
        toast.error(err instanceof ApiError ? err.message : "Couldn't save to Documents.");
      }
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Save to Documents</DialogTitle>
          <DialogDescription>
            File the generated PDF into this client's document archive.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          <div className="grid gap-2">
            <Label htmlFor="doc-name">Document name</Label>
            <Input
              id="doc-name"
              value={name}
              disabled={collision}
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="doc-folder">Folder</Label>
            <Select value={folderId} onValueChange={setFolderId}>
              <SelectTrigger id="doc-folder">
                <SelectValue placeholder="Select a folder" />
              </SelectTrigger>
              <SelectContent>
                {folders.map((f) => (
                  <SelectItem key={f.id} value={f.id}>
                    {f.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {collision && (
            <label className="flex items-center gap-2 text-sm">
              <Checkbox
                checked={overwrite}
                onCheckedChange={(v) => setOverwrite(v === true)}
              />
              Overwrite the existing document with this name
            </label>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleConfirm} disabled={saving || !name.trim() || !folderId}>
            {saving ? "Saving…" : collision ? "Overwrite & Save" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
